import React, { useEffect, useState } from 'react';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { RadioGroup } from 'formik-material-ui';
import { find, filter } from 'lodash';
import * as Yup from 'yup';

import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import FullScreenDialog, { FullScreenDialogProps } from '../FullScreenDialog';
import * as StoreService from '../../api/storeService';
import * as ProfileService from '../../api/profileService';
import * as RestrictionService from '../../api/restrictionService';
import * as EmploymentService from '../../api/employmentService';
import useCompanyContext from '../../hooks/useCompanyContext';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: theme.spacing(6),
  },
  logoContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  merchantLogo: {
    height: 120,
    width: 120,
    borderRadius: '50%',
    objectFit: 'cover',
    boxShadow: '0 2px 10px rgba(199, 199, 199, 0.5)',
  },
  header: {
    marginTop: theme.spacing(2),
  },
  actionContainer: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  continueButton: {
    width: 150,
  },
  address: {
    marginLeft: 25,
  },
}));

export interface Values {
  selectedStoreId: string;
}

const ValidationSchema = Yup.object().shape({
  selectedStoreId: Yup.string().required('Required'),
});

const initialValues: Values = {
  selectedStoreId: '',
};

type Props = Pick<FullScreenDialogProps, 'open'> & {
  onClose: () => void;
  onSuccess: (selectedStore: StoreService.Store) => void;
  profile: ProfileService.Profile;
  employment: EmploymentService.Employment;
};

export default function SelectStoreDialog({
  open,
  onClose,
  onSuccess,
  profile,
  employment,
}: Props) {
  const classes = useStyles();
  const { company } = useCompanyContext();
  const [storeList, setStoreList] = useState<StoreService.Store[] | null>(null);

  useEffect(() => {
    const fetch = async () => {
      if (company && profile && employment) {
        try {
          // fetch restriction list
          const resRestrictionList =
            await RestrictionService.listEmployeeRestriction(
              company.id,
              employment.id
            );
          // fetch store list
          const res = await StoreService.list(company.id);

          if (resRestrictionList && resRestrictionList.length > 0) {
            const restrictionStoreLimit = resRestrictionList.find(
              (x) => x.type === 'store_access'
            );
            if (
              restrictionStoreLimit &&
              restrictionStoreLimit.restriction.length > 0
            ) {
              setStoreList(
                filter(res, function (store) {
                  return restrictionStoreLimit.restriction.includes(store.id);
                })
              );
            } else {
              setStoreList(res);
            }
          } else {
            setStoreList(res);
          }
        } catch (e) {
          // TODO - Handle error
          console.log(e);
        }
      }
    };

    if (open) {
      fetch();
    }
  }, [company, open, profile, employment]);

  const handleSubmit = async (values: Values, _: FormikHelpers<Values>) => {
    if (storeList) {
      const selectedStore = find(storeList, function (store) {
        return store.id === values.selectedStoreId;
      });
      if (selectedStore) {
        onSuccess(selectedStore);
      }
    }
    onClose();
  };

  // TODO - Display a loader
  if (!storeList) {
    return null;
  }

  if (!company) {
    return null;
  }

  return (
    <FullScreenDialog open={open} onClose={onClose}>
      <div className={classes.logoContainer}>
        <img
          src={company.logo?.versions.xl}
          className={classes.merchantLogo}
          alt="flex reward"
        />
        <Typography variant="h4" className={classes.header}>
          SELECT STORE
        </Typography>
        <Typography
          variant="h5"
          className={classes.header}
          color="textSecondary"
        >
          WHERE WILL THIS DEVICE BE USED?
        </Typography>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={ValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form noValidate>
            <Container maxWidth="md">
              <div className={classes.container}>
                <Field component={RadioGroup} name="selectedStoreId">
                  {storeList.map((store) => (
                    <>
                      <FormControlLabel
                        key={store.id}
                        value={store.id}
                        control={
                          <Radio disabled={isSubmitting} color="primary" />
                        }
                        label={store.name}
                        disabled={isSubmitting}
                      />
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        className={classes.address}
                        key={store.id}
                      >
                        {store.address}
                      </Typography>
                    </>
                  ))}
                </Field>
              </div>
              <div className={classes.actionContainer}>
                <Button
                  type="submit"
                  color="primary"
                  className={classes.continueButton}
                  disabled={isSubmitting}
                >
                  Continue
                </Button>
              </div>
            </Container>
          </Form>
        )}
      </Formik>
    </FullScreenDialog>
  );
}
